import { Link, useSearchParams } from 'react-router-dom';
import { partner } from '../../api/partner';
import type { ReceivedHandover } from '../../api/types';
import { ErrorBlock } from '../../components/ErrorBlock';
import { ChevronIcon } from '../../components/Icons';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { useT, type DictKey } from '../../i18n';
import { useFormat } from '../../lib/format';
import { useAsync, useOnVisible } from '../../lib/useAsync';

export type HandoverTab = 'offered' | 'accepted' | 'done';
const TABS: { key: HandoverTab; label: DictKey; empty: DictKey }[] = [
  { key: 'offered', label: 'ho.tab.offered', empty: 'ho.empty.offered' },
  { key: 'accepted', label: 'ho.tab.accepted', empty: 'ho.empty.accepted' },
  { key: 'done', label: 'ho.tab.done', empty: 'ho.empty.done' },
];

export function tabOf(h: ReceivedHandover): HandoverTab {
  if (h.status === 'offered') return 'offered';
  if (h.status === 'accepted') return 'accepted';
  return 'done';
}

export function routeText(h: ReceivedHandover, country: (c: string | null) => string): string {
  const r = h.route;
  if (!r) return '';
  const a = [r.from_place, country(r.origin_country)].filter(Boolean).join(', ');
  const b = [r.to_place, country(r.dest_country)].filter(Boolean).join(', ');
  return a || b ? `${a || '…'} → ${b || '…'}` : '';
}

export function HandoversPage() {
  const t = useT();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find((x) => x.key === sp.get('tab'))?.key ?? 'accepted') as HandoverTab;
  const q = useAsync(() => partner.handovers(), []);
  useOnVisible(() => void q.reload());
  const items = (q.data ?? []).filter((h) => tabOf(h) === tab);
  const counts = Object.fromEntries(TABS.map((x) => [x.key, (q.data ?? []).filter((h) => tabOf(h) === x.key).length]));
  return (
    <>
      <PageHeader title={t('ho.title')} />
      <div role="tablist" className="mb-4 grid grid-cols-3 gap-2">
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tab === x.key}
            onClick={() => setSp({ tab: x.key }, { replace: true })}
            className={`min-h-tap rounded-xl border px-2 text-[15px] font-medium ${tab === x.key ? 'border-fg bg-acc text-acc-fg' : 'border-line bg-card'}`}
          >
            {t(x.label)}
            {counts[x.key] ? ` · ${counts[x.key]}` : ''}
          </button>
        ))}
      </div>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorBlock error={q.error} onRetry={() => void q.reload()} /> : null}
      {q.data && !items.length ? <p className="text-lab">{t(TABS.find((x) => x.key === tab)!.empty)}</p> : null}
      <ul className="space-y-3">
        {items.map((h) => (
          <li key={h.id}>
            <HandoverCard h={h} />
          </li>
        ))}
      </ul>
    </>
  );
}

export function HandoverCard({ h, hint }: { h: ReceivedHandover; hint?: string }) {
  const t = useT();
  const f = useFormat();
  const route = routeText(h, f.country);
  return (
    <Link to={`/handovers/${h.id}`} className="card flex items-center gap-3 active:bg-hov">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[17px] font-semibold">{h.case?.number ?? '—'}</span>
          <span className="chip">{t(`ho.scope.${h.scope}` as DictKey)}</span>
          {h.progress?.stage ? <span className="chip">{t(`ho.stage.${h.progress.stage}` as DictKey)}</span> : null}
        </div>
        {h.case?.deceased?.full_name ? <p className="mt-1 text-[16px]">{h.case.deceased.full_name}</p> : null}
        {route ? <p className="text-[15px] text-txt">{route}</p> : null}
        <p className="text-[14px] text-lab">
          {h.from_org?.name ? t('ho.from', { org: h.from_org.name }) : null} · {f.date(h.offered_at, false)}
        </p>
        {hint ? <p className="mt-1 text-[14px] font-medium">{hint}</p> : null}
      </div>
      <ChevronIcon />
    </Link>
  );
}
